/**
 * ShapeCard — Renders shape content based on type.
 * Pure inline styles, no external CSS.
 */

import React, { useEffect, useRef, useState } from 'react'
import type { BoardShape, DrawPoint } from './types'
import { useBoard } from './useBoard'
import { useSceneStore } from '../../store/scenes'
import { getDocumentThumbnailUrl } from '../../lib/paperless/api'

// ── Color map ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  scene: '#3b82f6',
  audit: '#0891b2',
  doc: '#6b7280',
  report: '#7c3aed',
  note: '#f59e0b',
  checklist: '#22c55e',
}

// ── Base card wrapper ────────────────────────────────────────────────────────

const cardBase: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const bodyStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#555',
  flex: 1,
  overflow: 'hidden',
}

const headerStyle = (color: string): React.CSSProperties => ({
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  color,
  letterSpacing: '0.04em',
  marginBottom: 2,
})

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  shape: BoardShape
}

export const ShapeCard = React.memo(function ShapeCard({ shape }: Props) {
  const color = TYPE_COLORS[shape.type] || '#888'

  // Transparent/borderless types — no card wrapper
  if (shape.type === 'draw') return <DrawContent shape={shape} />
  if (shape.type === 'arrow') return <ArrowContent shape={shape} />
  if (shape.type === 'line') return <LineContent shape={shape} />
  if (shape.type === 'frame') return <FrameContent shape={shape} />

  // Types with their own wrapper styling
  if (shape.type === 'text') return <TextContent shape={shape} />
  if (shape.type === 'shape') return <GeometricShapeContent shape={shape} />
  if (shape.type === 'image') return <ImageContent shape={shape} />
  if (shape.type === 'sticky') return <StickyContent shape={shape} />

  return (
    <div
      style={{
        ...cardBase,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {shape.type === 'scene' && <SceneContent shape={shape} color={color} />}
        {shape.type === 'audit' && <AuditContent shape={shape} color={color} />}
        {shape.type === 'doc' && <DocContent shape={shape} color={color} />}
        {shape.type === 'report' && <ReportContent shape={shape} color={color} />}
        {shape.type === 'note' && <NoteContent shape={shape} color={color} />}
        {shape.type === 'checklist' && <ChecklistContent shape={shape} color={color} />}
      </div>
    </div>
  )
})

// ── Scene ────────────────────────────────────────────────────────────────────

function SceneContent({ shape, color }: { shape: BoardShape; color: string }) {
  const scene = useSceneStore((s) => s.scenes.find((sc) => sc.id === shape.props.sceneId))
  const thumb = scene?.thumbnailHigh || scene?.thumbnailMedium || null

  return (
    <>
      <div style={headerStyle(color)}>Szene</div>
      <div style={titleStyle}>{shape.props.title || scene?.title || 'Szene'}</div>
      <div style={{ flex: 1, marginTop: 6, borderRadius: 4, overflow: 'hidden', minHeight: 0 }}>
        {thumb ? (
          <img
            src={thumb}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', background: '#1f2937', borderRadius: 4 }} />
        )}
      </div>
    </>
  )
}

// ── Audit ────────────────────────────────────────────────────────────────────

function AuditContent({ shape, color }: { shape: BoardShape; color: string }) {
  const auditId = shape.props.auditId as string | undefined
  const audits: any[] = (window as any)._auditObjects || []
  const audit = auditId ? audits.find((a) => a.id === auditId) : null
  const fields = audit?.data || {}

  return (
    <>
      <div style={headerStyle(color)}>Audit</div>
      <div style={titleStyle}>
        {shape.props.title || (fields.bezeichnung as string) || (fields.title as string) || 'Audit'}
      </div>
      <div style={{ ...bodyStyle, marginTop: 4 }}>
        {Object.entries(fields).slice(0, 4).map(([k, v]) => (
          <div key={k} style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ fontWeight: 500 }}>{k}:</span> {String(v)}
          </div>
        ))}
      </div>
    </>
  )
}

// ── Doc ──────────────────────────────────────────────────────────────────────

function DocContent({ shape, color }: { shape: BoardShape; color: string }) {
  const docId = shape.props.docId as string | undefined
  const numId = docId ? parseInt(docId, 10) : NaN
  const thumbUrl = !isNaN(numId) ? getDocumentThumbnailUrl(numId) : null
  const [err, setErr] = useState(false)

  return (
    <>
      <div style={headerStyle(color)}>Dokument</div>
      <div style={titleStyle}>{shape.props.title || 'Dokument'}</div>
      <div style={{ flex: 1, marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
        {thumbUrl && !err ? (
          <img
            src={thumbUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
            onError={() => setErr(true)}
          />
        ) : (
          <span style={{ fontSize: 48 }}>📄</span>
        )}
      </div>
    </>
  )
}

// ── Report ───────────────────────────────────────────────────────────────────

function ReportContent({ shape, color }: { shape: BoardShape; color: string }) {
  return (
    <>
      <div style={headerStyle(color)}>📊 Report</div>
      <div style={titleStyle}>{shape.props.title || 'Report'}</div>
      {shape.props.excerpt && (
        <div
          style={{
            ...bodyStyle,
            marginTop: 4,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {shape.props.excerpt}
        </div>
      )}
    </>
  )
}

// ── Note ─────────────────────────────────────────────────────────────────────

function NoteContent({ shape, color }: { shape: BoardShape; color: string }) {
  const tags: string[] = shape.props.tags || []

  return (
    <>
      <div style={headerStyle(color)}>Notiz</div>
      <div style={titleStyle}>{shape.props.title || 'Notiz'}</div>
      {shape.props.type && (
        <div style={{ marginTop: 4 }}>
          <span style={{
            fontSize: 10,
            background: '#fef3c7',
            color: '#92400e',
            padding: '1px 6px',
            borderRadius: 9999,
            fontWeight: 500,
          }}>
            {shape.props.type}
          </span>
        </div>
      )}
      {tags.length > 0 && (
        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {tags.map((t) => (
            <span key={t} style={{
              fontSize: 10,
              background: '#e5e7eb',
              color: '#374151',
              padding: '1px 5px',
              borderRadius: 9999,
            }}>
              {t}
            </span>
          ))}
        </div>
      )}
    </>
  )
}

// ── Checklist ────────────────────────────────────────────────────────────────

interface ChecklistItem {
  id: string
  text: string
  checked: boolean
}

function ChecklistContent({ shape, color }: { shape: BoardShape; color: string }) {
  const updateShape = useBoard((s) => s.updateShape)
  const items: ChecklistItem[] = shape.props.items || []
  const doneCount = items.filter((i) => i.checked).length
  const [newItemId, setNewItemId] = useState<string | null>(null)
  const itemRefsMap = useRef<Map<string, HTMLSpanElement>>(new Map())

  const toggleItem = (itemId: string) => {
    const newItems = items.map((i) =>
      i.id === itemId ? { ...i, checked: !i.checked } : i,
    )
    updateShape(shape.id, { props: { ...shape.props, items: newItems } })
  }

  const updateItemText = (itemId: string, text: string) => {
    const newItems = items.map((i) =>
      i.id === itemId ? { ...i, text } : i,
    )
    updateShape(shape.id, { props: { ...shape.props, items: newItems } })
  }

  const updateTitle = (text: string) => {
    updateShape(shape.id, { props: { ...shape.props, title: text } })
  }

  const addItem = (e: React.MouseEvent) => {
    e.stopPropagation()
    const id = crypto.randomUUID()
    const newItems = [...items, { id, text: 'Neuer Punkt', checked: false }]
    updateShape(shape.id, { props: { ...shape.props, items: newItems } })
    setNewItemId(id)
  }

  // Auto-focus newly added item
  useEffect(() => {
    if (newItemId) {
      const el = itemRefsMap.current.get(newItemId)
      if (el) {
        el.focus()
        // Select all text for easy replacement
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
      setNewItemId(null)
    }
  }, [newItemId, items])

  return (
    <>
      <div style={headerStyle(color)}>Checkliste</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => {
            const text = e.currentTarget.textContent || ''
            if (text.trim()) updateTitle(text)
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() } }}
          style={{ ...titleStyle, outline: 'none', cursor: 'text', minWidth: 20 }}
        >
          {shape.props.title || 'Checkliste'}
        </div>
        <span style={{ fontSize: 11, color: '#888', flexShrink: 0, marginLeft: 4 }}>
          {doneCount}/{items.length}
        </span>
      </div>
      <div style={{ flex: 1, marginTop: 4, overflow: 'auto', minHeight: 0 }}>
        {items.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              padding: '2px 0',
              textDecoration: item.checked ? 'line-through' : 'none',
              color: item.checked ? '#aaa' : '#333',
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={item.checked}
              onChange={() => toggleItem(item.id)}
              style={{ margin: 0, cursor: 'pointer', flexShrink: 0 }}
            />
            <span
              ref={(el) => { if (el) itemRefsMap.current.set(item.id, el); else itemRefsMap.current.delete(item.id) }}
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => {
                const text = e.currentTarget.textContent || ''
                if (text.trim()) updateItemText(item.id, text)
              }}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() } }}
              style={{
                outline: 'none',
                cursor: 'text',
                flex: 1,
                minWidth: 0,
              }}
            >
              {item.text}
            </span>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addItem}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          marginTop: 4,
          fontSize: 11,
          background: 'none',
          border: '1px dashed #ccc',
          borderRadius: 4,
          padding: '3px 8px',
          cursor: 'pointer',
          color: '#888',
          width: '100%',
        }}
      >
        + Hinzufuegen
      </button>
    </>
  )
}

// ── Draw (freehand) ──────────────────────────────────────────────────────

function DrawContent({ shape }: { shape: BoardShape }) {
  const points: DrawPoint[] = shape.props.points || []
  const color: string = shape.props.color || '#333'
  const strokeWidth: number = shape.props.strokeWidth || 2

  const pointsStr = points.map((p) => `${p.x},${p.y}`).join(' ')

  return (
    <svg
      width={shape.w}
      height={shape.h}
      viewBox={`0 0 ${shape.w} ${shape.h}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <polyline
        points={pointsStr}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ── Text ─────────────────────────────────────────────────────────────────

function TextContent({ shape }: { shape: BoardShape }) {
  const updateShape = useBoard((s) => s.updateShape)
  const ref = useRef<HTMLDivElement>(null)
  const fontSize: number = shape.props.fontSize || 16
  const color: string = shape.props.color || '#333'

  // Auto-focus on creation (when text is empty)
  useEffect(() => {
    if (shape.props.text === '' && ref.current) {
      ref.current.focus()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => {
        updateShape(shape.id, {
          props: { ...shape.props, text: e.currentTarget.textContent || '' },
        })
      }}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        height: '100%',
        fontSize,
        color,
        outline: 'none',
        border: 'none',
        background: 'transparent',
        cursor: 'text',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        padding: 4,
        boxSizing: 'border-box',
      }}
    >
      {shape.props.text}
    </div>
  )
}

// ── Shape (geometric: rect, ellipse, diamond, triangle, star) ────────────

function GeometricShapeContent({ shape }: { shape: BoardShape }) {
  const fill: string = shape.props.fill || 'transparent'
  const stroke: string = shape.props.stroke || '#333'
  const strokeWidth: number = shape.props.strokeWidth || 2
  const subType: string = shape.props.shapeType || 'rect'

  if (subType === 'rect') {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: fill,
          border: `${strokeWidth}px solid ${stroke}`,
          borderRadius: 4,
          boxSizing: 'border-box',
        }}
      />
    )
  }

  const w = shape.w
  const h = shape.h

  let svgContent: React.ReactNode = null

  if (subType === 'ellipse') {
    svgContent = (
      <ellipse
        cx={w / 2}
        cy={h / 2}
        rx={w / 2 - strokeWidth}
        ry={h / 2 - strokeWidth}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    )
  } else if (subType === 'diamond') {
    const pts = `${w / 2},${strokeWidth} ${w - strokeWidth},${h / 2} ${w / 2},${h - strokeWidth} ${strokeWidth},${h / 2}`
    svgContent = (
      <polygon
        points={pts}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    )
  } else if (subType === 'triangle') {
    const pts = `${w / 2},${strokeWidth} ${w - strokeWidth},${h - strokeWidth} ${strokeWidth},${h - strokeWidth}`
    svgContent = (
      <polygon
        points={pts}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    )
  } else if (subType === 'star') {
    // 5-pointed star
    const cx = w / 2
    const cy = h / 2
    const outerR = Math.min(w, h) / 2 - strokeWidth
    const innerR = outerR * 0.38
    const pts: string[] = []
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outerR : innerR
      const angle = (Math.PI / 2) + (i * Math.PI) / 5
      pts.push(`${cx + r * Math.cos(angle)},${cy - r * Math.sin(angle)}`)
    }
    svgContent = (
      <polygon
        points={pts.join(' ')}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    )
  }

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {svgContent}
    </svg>
  )
}

// ── Line (like arrow but without arrowhead) ──────────────────────────────

function LineContent({ shape }: { shape: BoardShape }) {
  const x2: number = shape.props.x2 ?? 0
  const y2: number = shape.props.y2 ?? 0
  const color: string = shape.props.color || '#333'
  const strokeWidth: number = shape.props.strokeWidth || 2

  const startX = shape.w - x2
  const startY = shape.h - y2

  return (
    <svg
      width={shape.w}
      height={shape.h}
      viewBox={`0 0 ${shape.w} ${shape.h}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <line
        x1={startX}
        y1={startY}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  )
}

// ── Frame (dashed container with title) ──────────────────────────────────

function FrameContent({ shape }: { shape: BoardShape }) {
  const title: string = shape.props.title || 'Frame'
  const borderColor: string = shape.props.borderColor || '#999'

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        border: `2px dashed ${borderColor}`,
        borderRadius: 4,
        boxSizing: 'border-box',
        position: 'relative',
        background: 'transparent',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: -20,
          left: 4,
          fontSize: 11,
          fontWeight: 600,
          color: borderColor,
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>
    </div>
  )
}

// ── Image ────────────────────────────────────────────────────────────────

function ImageContent({ shape }: { shape: BoardShape }) {
  const src: string = shape.props.src || ''
  const alt: string = shape.props.alt || ''

  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        borderRadius: 4,
      }}
      draggable={false}
    />
  )
}

// ── Arrow ────────────────────────────────────────────────────────────────

function ArrowContent({ shape }: { shape: BoardShape }) {
  const updateShape = useBoard((s) => s.updateShape)
  const x2: number = shape.props.x2 ?? 0
  const y2: number = shape.props.y2 ?? 0
  const color: string = shape.props.color || '#333'
  const strokeWidth: number = shape.props.strokeWidth || 2
  const curvature: number = shape.props.curvature ?? 0

  const startX = shape.w - x2
  const startY = shape.h - y2
  const markerId = `arrow-${shape.id}`

  // Midpoint of straight line
  const midX = (startX + x2) / 2
  const midY = (startY + y2) / 2

  // Perpendicular direction
  const dx = x2 - startX
  const dy = y2 - startY
  const len = Math.sqrt(dx * dx + dy * dy) || 1
  const perpX = -dy / len
  const perpY = dx / len

  // Control point offset: curvature factor (default 0) + base 20% of length
  const offset = curvature !== 0 ? curvature : 0
  const cpX = midX + perpX * offset
  const cpY = midY + perpY * offset

  // Tangent at endpoint for marker orientation: derivative of Q bezier at t=1 is 2*(P2-CP)
  // orient="auto" on the marker handles this automatically for path

  const pathD = `M ${startX} ${startY} Q ${cpX} ${cpY} ${x2} ${y2}`

  // Draggable control point position (in SVG coords)
  const [dragging, setDragging] = useState(false)

  const onCpPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setDragging(true)
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  const onCpPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return
    e.stopPropagation()
    // Get SVG-relative coordinates
    const svgEl = (e.target as HTMLElement).closest('svg')
    if (!svgEl) return
    const rect = svgEl.getBoundingClientRect()
    const scaleX = shape.w / rect.width
    const scaleY = shape.h / rect.height
    const localX = (e.clientX - rect.left) * scaleX
    const localY = (e.clientY - rect.top) * scaleY
    // Project onto perpendicular axis to get curvature
    const newOffset = (localX - midX) * perpX + (localY - midY) * perpY
    updateShape(shape.id, { props: { ...shape.props, curvature: Math.round(newOffset) } })
  }

  const onCpPointerUp = (e: React.PointerEvent) => {
    if (dragging) {
      setDragging(false)
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    }
  }

  return (
    <svg
      width={shape.w}
      height={shape.h}
      viewBox={`0 0 ${shape.w} ${shape.h}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <marker
          id={markerId}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill={color} />
        </marker>
      </defs>
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        markerEnd={`url(#${markerId})`}
        strokeLinecap="round"
      />
      {/* Draggable control point */}
      <circle
        cx={cpX}
        cy={cpY}
        r={5}
        fill="rgba(59,130,246,0.3)"
        stroke="#3b82f6"
        strokeWidth={1}
        style={{ cursor: 'grab' }}
        onPointerDown={onCpPointerDown}
        onPointerMove={onCpPointerMove}
        onPointerUp={onCpPointerUp}
      />
    </svg>
  )
}

// ── Sticky note ──────────────────────────────────────────────────────────

function StickyContent({ shape }: { shape: BoardShape }) {
  const updateShape = useBoard((s) => s.updateShape)
  const ref = useRef<HTMLDivElement>(null)
  const bgColor: string = shape.props.color || '#fef08a'

  // Auto-focus on creation
  useEffect(() => {
    if (shape.props.text === '' && ref.current) {
      ref.current.focus()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: bgColor,
        borderRadius: 4,
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        padding: 12,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => {
          updateShape(shape.id, {
            props: { ...shape.props, text: e.currentTarget.textContent || '' },
          })
        }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          fontSize: 14,
          color: '#333',
          outline: 'none',
          border: 'none',
          background: 'transparent',
          cursor: 'text',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: 'auto',
        }}
      >
        {shape.props.text}
      </div>
    </div>
  )
}
